import type { PartialBy } from '@ipfabric/utils/types/PartialBy';
import { makeTypeGuardByPropertyValue } from '@ipfabric/utils/types/makeTypeGuardByPropertyValue';
import type { ReactNode } from 'react';
import {
  type InteractiveAtomAccent,
  type InteractiveAtomBaseProps,
  type InteractiveAtomRenderedAsAnchorProps,
  type InteractiveAtomRenderedAsButtonProps,
  type InteractiveAtomRenderedAsLinkProps,
  type InteractiveAtomSize,
  fromInteractiveAtomToIconSize,
} from '../utils/InteractiveAtomProps';

export type LinkSize = InteractiveAtomSize;
export type LinkAccent = InteractiveAtomAccent | 'contrast';

export const fromLinkToIconSize = fromInteractiveAtomToIconSize;

export type LinkBaseProps = Omit<InteractiveAtomBaseProps, 'accent'> & {
  accent?: LinkAccent;
  children?: ReactNode;
};

export type LinkRenderedAsButtonProps =
  InteractiveAtomRenderedAsButtonProps<LinkBaseProps>;

export type LinkRenderedAsAnchorProps =
  InteractiveAtomRenderedAsAnchorProps<LinkBaseProps>;

// make 'renderAs' optional with 'link' fallback value defined in the component
export type LinkRenderedAsLinkProps = PartialBy<
  InteractiveAtomRenderedAsLinkProps<LinkBaseProps>,
  'renderAs'
>;

export type LinkProps =
  | LinkRenderedAsButtonProps
  | LinkRenderedAsAnchorProps
  | LinkRenderedAsLinkProps;

export const isLinkRenderedAsButton = makeTypeGuardByPropertyValue<
  LinkProps,
  LinkRenderedAsButtonProps
>()('renderAs', 'button');

export const isLinkRenderedAsAnchor = makeTypeGuardByPropertyValue<
  LinkProps,
  LinkRenderedAsAnchorProps
>()('renderAs', 'anchor');

export const isLinkRenderedAsLink = makeTypeGuardByPropertyValue<
  LinkProps,
  LinkRenderedAsLinkProps
>()('renderAs', 'link');

export type ExternalLinkProps = Omit<
  LinkRenderedAsAnchorProps,
  'renderAs' | 'icon' | 'iconPosition' | 'iconStyle'
>;

export type PageLinkProps = Omit<
  LinkRenderedAsLinkProps,
  'renderAs' | 'icon' | 'iconPosition' | 'iconStyle'
>;
